import React from 'react'

import clsx from 'clsx'

import Button from 'components/Button'
import Collapse from 'components/Collapse'
import CtaButton from 'components/CtaButton'
import Icon from 'components/Icon'
import Paper from 'components/Paper'
import { useEventContext } from 'context/EventContext'

import AttributeList, { ATTRIBUTE_VARIANTS } from '../../../AttributeList'

import classes from './SectionWithExpand.module.scss'

const noop = () => {}
const VISIBLE_ATTRIBUTES = 10

/** @type {import("./types").default} */
const SectionWithExpand = ({
  category,
  isCtaFullWidth = false,
  isCtaTextButton = false,
  isHighest,
  gridSize = 1,
  gridIndex = 0,
  listingNames,
  listingsNames,
  expanded = false,
  onExpandButtonClick = noop,
  href,
  isMobileTabletLayout = false,
}) => {
  const { addEvent } = useEventContext()
  const isWinner = isHighest && gridSize > 1

  const titleAttribute = [
    {
      label: (
        <>
          <span className={clsx({ [classes.responsiveHeaderline]: gridSize !== 1 })}>Total </span>
          {category}
        </>
      ),
      variant: `${isWinner ? ATTRIBUTE_VARIANTS.crown : ATTRIBUTE_VARIANTS.none}`,
      text: `${listingNames.length > 0 ? listingNames.length : 'N/A'}`,
      isBold: true,
    },
  ]

  const ExpandButton = () => (
    <Button
      className={classes.expandButton}
      color="primary"
      onClick={onExpandButtonClick}
      {...addEvent({
        engagementComponent: `product_${category}`,
        engagementDestination: expanded ? 'collapse-text' : 'expand-text',
        engagementDetails: expanded ? 'expand-button_collapse-list' : 'expand-button_expand-list',
        name: 'engagement_product_click',
      })}
    >
      <span>{expanded ? 'Collapse list' : 'Expand list'}</span>
      <Icon icon={expanded ? 'chevron-up' : 'chevron-down'} size="12" className={classes.chevronIcon} />
    </Button>
  )

  const expandButtonAttribute = [
    {
      label: <ExpandButton />,
      variant: ATTRIBUTE_VARIANTS.none,
    },
  ]

  const attributes = listingsNames.map(facetName => ({
    label: facetName,
    variant: listingNames.includes(facetName) ? ATTRIBUTE_VARIANTS.check : ATTRIBUTE_VARIANTS.uncheck,
  }))

  const disableButton = listingNames.length === 0 && category === 'integrations'

  const labelFeatures = listingNames.length === 0 ? 'View details' : 'View all features'
  const labelIntegrations = 'see full list'
  const ctaLabel = category === 'integrations' ? labelIntegrations : labelFeatures
  const ctaShortLabel = category === 'integrations' ? 'see full list' : 'View details'

  return (
    <Paper className={clsx('SectionWithExpand', classes.container)} elevation={0}>
      <div
        className={clsx(classes.listAttributeTitle, {
          [classes.listAttributeTitleCrowned]: isWinner,
        })}
      >
        <AttributeList
          attributes={titleAttribute}
          className={classes.listAttributeOverwrite}
          gridSize={gridSize}
          gridIndex={gridIndex}
        />
      </div>
      <div className={classes.listAttribute}>
        <AttributeList attributes={attributes.slice(0, VISIBLE_ATTRIBUTES)} gridSize={gridSize} gridIndex={gridIndex} />
        {attributes.length > VISIBLE_ATTRIBUTES && (
          <>
            <Collapse open={expanded} className={classes.relativeContainer}>
              <AttributeList
                attributes={attributes.slice(VISIBLE_ATTRIBUTES, attributes.length)}
                gridSize={gridSize}
                gridIndex={gridIndex}
              />
            </Collapse>
            {isMobileTabletLayout ? (
              <div className={clsx(classes.relativeContainer)}>
                <AttributeList attributes={expandButtonAttribute} gridSize={gridSize} gridIndex={gridIndex} />
              </div>
            ) : (
              <div className={classes.centered}>
                <ExpandButton />
              </div>
            )}
          </>
        )}
      </div>
      <div className={href ? classes.button : classes.removedButton}>
        {href && (
          <CtaButton
            data-testid={`${category}-cta-${isMobileTabletLayout ? 'mobile' : 'desktop'}`}
            disabled={disableButton}
            fullWidth={isCtaFullWidth}
            label={ctaLabel}
            shortLabel={ctaShortLabel}
            secondary
            variant={isCtaTextButton ? 'text' : 'contained'}
            href={disableButton ? undefined : href}
            {...addEvent({
              engagementComponent: `product_${category}`,
              engagementDestination: `go-to_product_${category}`,
              engagementDetails: `button_view-all-${category}`,
              name: 'engagement_product_click',
            })}
          />
        )}
      </div>
    </Paper>
  )
}

export default SectionWithExpand
